import { Query, QueryStringParams } from '@/src/interfaces'
import { Physician } from '@/src/interfaces'

/**
 * Retrieves providers based on a search query.
 * @param {QueryStringParams} searchParams - The search query string parameters.
 * @param {Query} query - The search query.
 * @param {number} [timeoutMs=5000] - The timeout duration in milliseconds.
 * @throws {Error} If there is an issue with the API call.
 * @return {Promise<{ body: object[]; total: number }>} A promise that resolves to the search results.
 */
export const queryProvidersOpenSearch = async (
  searchParams: QueryStringParams,
  query: Query,
  timeoutMs: number = 5000,
  retryCount = 0,
  maxRetries = 3
): Promise<{ body: object[]; total: number }> => {
  try {
    // Try the first fetch with timeout
    const controller = new AbortController()
    const id = setTimeout(() => controller.abort(), timeoutMs)

    const response = await fetch('/api/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...query,
        queryParams: {
          ...searchParams,
        },
      }),
      signal: controller.signal,
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }

    clearTimeout(id)
    const data = await response.json()
    return data
  } catch (error) {
    if (
      typeof error === 'object' &&
      error !== null &&
      ('name' in error || 'message' in error) &&
      ((error as { name?: string }).name === 'AbortError' ||
        (error as { message?: string }).message === 'Timeout')
    ) {
      console.warn(
        `Initial fetch timed out, retrying... (Attempt ${
          retryCount + 1
        } of ${maxRetries})`
      )
      if (retryCount >= maxRetries) {
        throw new Error('Max retry attempts reached. Service is unresponsive.')
      }
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, retryCount) * 1000)
      ) // Exponential backoff
      return await queryProvidersOpenSearch(
        searchParams,
        query,
        timeoutMs,
        maxRetries,
        retryCount + 1
      )
    } else {
      console.error('Error during fetch:', error)
      throw error
    }
  }
}

/**
 * Makes a GET request to the OpenSearch API to search for a provider by NPI.
 * @param {string} npi - The NPI of the provider to search for.
 * @param {object} location - The location of the provider to search for.
 * @return {Promise<{ body: Physician[]; total: number }>} - A Promise that resolves to the API response data.
 * @throws {Error} - If the API call fails or the response is invalid.
 */
export const getProviderByNpi = async (
  npi: string,
  location?: { lat: number; lon: number }
): Promise<{ body: Physician[]; total: number }> => {
  try {
    const response = await fetch(
      `/api/physician?npi=${npi}${
        location ? `&lat=${location.lat}&lon=${location.lon}` : ''
      }`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error during fetch:', error)
    throw error
  }
}
